<template>
  <v-container fluid fill-height class="ma-0 pa-0 px-2">
    <v-select
      v-model="visitVariable"
      :items="visitItems"
      :disabled="!interactive"
      label="Select visit variable"
      color="blue"
      class="fit"
    >
    </v-select>

    <v-select
      v-model="firstVisit"
      :items="firstVisitEvents"
      :disabled="!interactive"
      label="Select first visit"
      class="fit ml-2"
    >
      <template v-slot:append>
        <v-icon :color="colors['firstVisit']">edit</v-icon>
      </template>
    </v-select>

    <v-select
      v-model="lastVisit"
      :items="lastVisitEvents"
      :disabled="!interactive"
      label="Select last visit"
      class="fit ml-2"
    >
      <template v-slot:append>
        <v-icon :color="colors['lastVisit']">edit</v-icon>
      </template>
    </v-select>

    <v-checkbox
      v-model="hideUnselectedVars"
      label="Hide unselected variables"
      class="fit ml-2"
    ></v-checkbox>

    <v-tooltip bottom color="primary">
      <template v-slot:activator="{ on: tooltip }">
        <v-chip
          class="title ml-4"
          :color="getNumSubjectsColor(numSelectedSubjects)"
          :text-color="getNumSubjectsTextColor(numSelectedSubjects)"
          v-on="{ ...tooltip }"
          >Study Population - {{ numSelectedSubjects }}</v-chip
        >
      </template>
      <span
        >{{ numSelectedSubjects }} subject(s) had measurements recorded for
        <span style="font-style: italic;">all</span> selected variables at the
        indicated first/last visit(s).</span
      >
    </v-tooltip>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { state, actions } from '@/store/modules/datasetSummary/types';
import {
  colors,
  getNumSubjectsColor,
  getNumSubjectsTextColor,
} from '@/utils/colors';
import { sortVisitEvents } from '@/utils/helpers';

export default {
  props: {
    interactive: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      visitItems: ['Visit Event', 'Visit Number'],
      uniqueEvents: [],
      firstVisitEvents: [],
      lastVisitEvents: [],
      numSubjects: 0,
      colors: colors,
      hideUnselectedVars: true,
      getNumSubjectsColor: getNumSubjectsColor,
      getNumSubjectsTextColor: getNumSubjectsTextColor,
    };
  },
  computed: {
    ...mapState('datasetSummary', {
      visitSummaries: state.VISIT_SUMMARIES,
      timesBetweenVisits: state.TIMES_BETWEEN_VISITS,
      numSelectedSubjects: state.NUM_SELECTED_SUBJECTS,
      firstVisits: state.FIRST_VISITS,
      lastVisits: state.LAST_VISITS,
      selVisitVariable: state.VISIT_VARIABLE,
      isCollection: state.IS_COLLECTION,
      selectedVariables: state.SELECTED_VARIABLES,
    }),
    observationVariables() {
      return this.selectedVariables.filter(svar => svar.type == 'observation');
    },
    visitVariable: {
      get() {
        return this.selVisitVariable;
      },
      set(value) {
        this.setVisitVariable(value);
        this.updateEvents();
        this.fetchTimesBetweenVisits();
      },
    },
    lastVisit: {
      get() {
        var varIds = this.getObservationVariableIds();
        var lastVisit = null;
        varIds.forEach(vid => {
          var lv = this.lastVisits[vid];
          if (lastVisit == null) {
            lastVisit = lv;
          }
          // different variables have different visits
          else if (lastVisit != lv) {
            lastVisit = 'multiple';
          }
        });
        return lastVisit;
      },
      set(value) {
        if (value != 'multiple') {
          this.setLastVisit(value);
        }
      },
    },
    firstVisit: {
      get() {
        var varIds = this.getObservationVariableIds();
        var firstVisit = null;
        varIds.forEach(vid => {
          var fv = this.firstVisits[vid];
          if (firstVisit == null) {
            firstVisit = fv;
          }
          // different variables have different visits
          else if (firstVisit != fv) {
            firstVisit = 'multiple';
          }
        });
        return firstVisit;
      },
      set(value) {
        if (value != 'multiple') {
          this.setFirstVisit(value);
        }
      },
    },
  },
  watch: {
    observationVariables() {
      this.fetchTimesBetweenVisits();
    },
    hideUnselectedVars(newval) {
      this.$emit('hideUnselectedVars', newval);
    },
    visitSummaries() {
      this.fetchTimesBetweenVisits();
    },
    firstVisits: {
      handler() {
        this.updateEvents();
        this.fetchTimesBetweenVisits();
      },
      deep: true,
    },
    lastVisits: {
      handler() {
        this.updateEvents();
        this.fetchTimesBetweenVisits();
      },
      deep: true,
    },
  },
  mounted() {
    this.updateEvents();
    this.fetchTimesBetweenVisits();
  },
  methods: {
    ...mapActions('datasetSummary', {
      setFirstVisit: actions.SET_FIRST_VISIT,
      setLastVisit: actions.SET_LAST_VISIT,
      setVisitVariable: actions.SET_VISIT_VARIABLE,
      fetchTimesBetweenVisits: actions.FETCH_TIMES_BETWEEN_VISITS,
    }),
    getObservationVariableIds() {
      return this.observationVariables.map(v => v.id);
    },
    updateEvents() {
      if (!this.visitSummaries) {
        return;
      }
      // Identify the unique events
      // TODO - ensure that these events are sorted chronologically
      var uniqueEvents = this.getUniqueList(
        this.getColumn(this.visitSummaries[this.visitVariable], 0)
      );
      this.uniqueEvents = sortVisitEvents(uniqueEvents);

      var varIds = this.getObservationVariableIds();
      var firstLastVisit = null;
      var lastFirstVisit = null;
      var firstVisits = {};
      var lastVisits = {};

      varIds.forEach(v => {
        firstVisits[this.firstVisits[v]] = true;
        lastVisits[this.lastVisits[v]] = true;
      });

      this.uniqueEvents.forEach(e => {
        if (e in firstVisits) {
          lastFirstVisit = e;
        }
        if (firstLastVisit == null && e in lastVisits) {
          firstLastVisit = e;
        }
      });

      // possible first visit events - all those up until the first lastVisit
      this.firstVisitEvents = ['multiple'];
      for (var i = 0; i < this.uniqueEvents.length; i++) {
        if (this.lastVisit && this.uniqueEvents[i] == firstLastVisit) {
          break;
        }
        this.firstVisitEvents.push(this.uniqueEvents[i]);
      }

      // possible last visit events - starting after the last firstVisit
      var firstVisitSeen = !this.firstVisit;
      this.lastVisitEvents = ['multiple'];
      for (i = 0; i < this.uniqueEvents.length; i++) {
        if (firstVisitSeen) {
          this.lastVisitEvents.push(this.uniqueEvents[i]);
        } else if (this.firstVisit && this.uniqueEvents[i] == lastFirstVisit) {
          firstVisitSeen = true;
        }
      }
    },
    getColumn(anArray, columnNumber) {
      return anArray.map(function(row) {
        return row[columnNumber];
      });
    },
    getUniqueList(anArray) {
      var unique = anArray.filter((v, i, a) => a.indexOf(v) === i);
      return unique;
    },
  },
};
</script>

<style scoped>
.horizontal {
  display: flex;
}
.scrollable {
  overflow-x: auto;
}
.scrollable::-webkit-scrollbar {
  display: none;
}
.v-select.fit {
  width: min-content;
}
.v-select.fit .v-select__selection--comma {
  text-overflow: unset;
}
</style>
